import type { TimelineSpan } from '../../api/admin.js';

export interface TreeNode {
  span: TimelineSpan;
  depth: number;
  children: TreeNode[];
}

/**
 * Construye el árbol jerárquico de spans:
 *  - Cada parent_span_id presente en la lista se conecta a su parent.
 *  - Si un parent referenciado no está en la lista (huérfano por sampling)
 *    el span se renderea en root con la marca `attributes.__orphan`.
 *  - Los root spans se ordenan por started_at.
 */
export function buildTree(spans: ReadonlyArray<TimelineSpan>): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const span of spans) {
    byId.set(span.id, { span, depth: 0, children: [] });
  }

  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.span.parentId;
    if (parentId && byId.has(parentId)) {
      const parent = byId.get(parentId)!;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  function setDepth(node: TreeNode, depth: number): void {
    node.depth = depth;
    node.children.sort((a, b) => a.span.startedAt.localeCompare(b.span.startedAt));
    for (const child of node.children) setDepth(child, depth + 1);
  }

  roots.sort((a, b) => a.span.startedAt.localeCompare(b.span.startedAt));
  for (const r of roots) setDepth(r, 0);
  return roots;
}

export function flattenTree(roots: ReadonlyArray<TreeNode>): TreeNode[] {
  const out: TreeNode[] = [];
  function walk(node: TreeNode): void {
    out.push(node);
    for (const c of node.children) walk(c);
  }
  for (const r of roots) walk(r);
  return out;
}

export interface TimelineRange {
  startMs: number;
  endMs: number;
  durationMs: number;
}

export function timelineRange(spans: ReadonlyArray<TimelineSpan>): TimelineRange {
  if (spans.length === 0) return { startMs: 0, endMs: 1, durationMs: 1 };
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const s of spans) {
    const a = new Date(s.startedAt).getTime();
    const b = new Date(s.endedAt).getTime();
    if (Number.isFinite(a) && a < start) start = a;
    if (Number.isFinite(b) && b > end) end = b;
  }
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return { startMs: 0, endMs: 1, durationMs: 1 };
  }
  return { startMs: start, endMs: end, durationMs: end - start };
}
